import type { Availability, BookingUpsellMenuItem } from '../../../domain/Availability'
import type { BookingPaymentRequirement } from '../../../domain/BookingSlotOption'
import { BookingRequest } from '../../../domain/BookingRequest'
import { bookingPaymentTerminologyForVenueId } from '../../../domain/BookingPaymentTerminology'
import { Time } from '../../../domain/Time'
import type { Venue } from '../../../domain/Venue'
import { Helper } from '../../../utilities/helper'
import type { VenueStorefrontBookingSummary } from '../venue-storefront-booking-summary-card/venue-storefront-booking-summary-card.component'
import type { StorefrontBookingContext } from './venue-storefront-booking-tracking.service'
import type {
    DetailsStepState,
    VenueStorefrontBookingLineItem,
    VenueStorefrontBookingFlowState,
    VenueStorefrontBookingChoice,
    VenueStorefrontBookingDetails,
    VenueStorefrontBookingSearchInitialValues,
} from './venue-storefront-booking-flow.types'

export function paidUpsellPaymentRequirement(
    availability: Availability,
    reasonId: string | null,
    lineItems: VenueStorefrontBookingLineItem[],
    eventId: string | null = null
): BookingPaymentRequirement | null {
    if (lineItems.length === 0 || eventId !== null) return null
    const account = availability.venue.paymentsAccountForCapability('DepositPayments')
    if (!availability.bookingUpsellMenu(reasonId)?.isPaid || !account || account.usesBreathePay()) {
        return null
    }
    const menuItems = availability.bookingUpsellMenuItems(reasonId)
    const amount = lineItems.reduce((total, line) => {
        const item = menuItems.find(item => item.productId === line.productId && item.skuId === line.skuId
            && (!line.menuItemId || line.menuItemId === item.menuItemId))
        return total + upsellUnitAmount(item, line) * line.quantity
    }, 0)
    if (amount <= 0) return null
    return {
        kind: 'deposit', amount, perCoverAmount: null, isPaidUpsell: true, orderAmount: amount, depositAmount: 0,
        refundCutOffHours: availability.venue.depositRefundCutOffDays != null
            ? availability.venue.depositRefundCutOffDays * 24 : null,
    }
}

export function includePaidOrder(requirement: BookingPaymentRequirement | null, order: BookingPaymentRequirement | null): BookingPaymentRequirement | null {
    if (!requirement) {
        return order
    }
    if (!order || requirement.orderAmount !== undefined) {
        return requirement
    }
    if (requirement.kind === 'card') {
        return { ...requirement, orderAmount: order.amount }
    }
    return { ...requirement, amount: requirement.amount + order.amount, depositAmount: requirement.amount,
        orderAmount: order.amount, isPaidUpsell: true }
}

function upsellUnitAmount(item: BookingUpsellMenuItem | undefined, lineItem: VenueStorefrontBookingLineItem): number {
    if (item?.paidOnline === false) {
        return 0
    }
    const modifiers = item?.modifierGroups.flatMap(group => group.modifiers) ?? []
    return (item?.amount ?? 0) + modifiers.reduce((total, modifier) => {
        return total + (lineItem.modifierSkuIds?.includes(modifier.skuId) ? modifier.amount : 0)
    }, 0)
}

export function makeBookingSummary(state: DetailsStepState, cdnUrl: string): VenueStorefrontBookingSummary | null {
    const area = state.selectedSlot.reservableArea
    const option = state.selectedSlot.reservableOption
    if (area === null || option === null) {
        return null
    }
    const event = state.eventId
        ? state.availability.venue.eventWithId(state.eventId)
        : null
    const reason = state.reasonId
        ? state.availability.venue.reasonWithId(state.reasonId)
        : null
    const menuItems = state.availability.bookingUpsellMenuItems(state.reasonId, state.eventId)
    const paidMenu = state.availability.bookingUpsellMenu(state.reasonId, state.eventId)?.isPaid
    const quotedOrder = state.paymentIntent?.order
    const venueImageId = state.availability.venue.promotionalImageIds?.[0]
    return {
        venueName: state.availability.venue.displayName,
        venueImageUrl: venueImageId
            ? `${cdnUrl}/1200x675/venue-promotional-images/${venueImageId}`
            : null,
        partySize: state.searchCriteria.partySize,
        dateTime: state.selectedSlot.bookingSlot.dateTime,
        endDateTime: state.selectedSlot.bookingSlot.endDateTime(option.durationMinutes),
        areaName: state.customerSelectedArea ? area.displayName : null,
        currencyCode: state.availability.venue.currencyCode,
        choice: event
            ? {
                displayName: event.displayName,
            }
            : reason
                ? {
                    displayName: reason.displayName,
                }
                : undefined,
        extras: quotedOrder?.lineItems.map(line => ({
            name: line.name, quantity: line.quantity, unitAmount: quotedOrder.isPaid && line.paidOnline ? line.unitAmount() : undefined,
            modifierNames: line.modifiers.map(modifier => modifier.name),
            ...(quotedOrder.isPaid && line.paidOnline ? { customerRefundableUntil: line.customerRefundableUntil } : {}),
        })) ?? state.lineItems.map(lineItem => {
            const item = menuItems.find(menuItem => {
                return menuItem.productId === lineItem.productId
                    && menuItem.skuId === lineItem.skuId
                    && (!lineItem.menuItemId || menuItem.menuItemId === lineItem.menuItemId)
            })
            return {
                name: item?.name ?? 'Item',
                quantity: lineItem.quantity,
                unitAmount: paidMenu && item?.paidOnline !== false
                    ? upsellUnitAmount(item, lineItem) : undefined,
                ...(paidMenu && item?.paidOnline !== false ? item?.refundable === false
                    ? { customerRefundableUntil: null } : { refundPolicyPending: true } : {}),
                modifierNames: item?.modifierGroups.flatMap(group => group.modifiers)
                    .filter(modifier => lineItem.modifierSkuIds?.includes(modifier.skuId))
                    .map(modifier => modifier.name) ?? [],
            }
        }),
        payment: state.paymentRequirement
            ? {
                kind: state.paymentRequirement.kind,
                amount: state.paymentRequirement.amount,
                label: bookingPaymentTerminologyForVenueId(
                    state.availability.venue.id,
                    state.paymentRequirement.kind === 'deposit' && state.paymentRequirement.isPaidUpsell
                ).label,
                isPerCover: state.paymentRequirement.kind === 'card'
                    ? state.paymentRequirement.isPerCover
                    : undefined,
            }
            : undefined,
    }
}

export function makeBookingRequest(
    state: DetailsStepState,
    details: VenueStorefrontBookingDetails,
    launchValues: VenueStorefrontBookingSearchInitialValues | null
): BookingRequest {
    const area = state.selectedSlot.reservableArea
    if (area === null) {
        throw new Error('A reservable area is required to create a booking request.')
    }
    return new BookingRequest(
        state.bookingRequestId,
        state.selectedSlot.bookingSlot,
        area.id,
        details.firstName,
        details.lastName,
        details.emailAddress,
        details.phoneNumber,
        state.searchCriteria.partySize,
        details.notes,
        details.agreedToMarketing,
        details.agreedToTerms,
        state.reasonId,
        state.eventId,
        state.lineItems,
        details.answers,
        null,
        state.searchCriteria.requiresWheelchairAccess,
        state.searchCriteria.requiresDogFriendly,
        state.searchCriteria.requiresPramAccessible,
        state.searchCriteria.requiresHighchairFriendly,
        launchValues?.utmSource ?? null,
        launchValues?.utmMedium ?? null,
        launchValues?.utmCampaign ?? null,
        null,
        [],
        details.agreedToMerchantPrivacy ?? null
    )
}

export function makeBookingQuery(query: string, searchCriteria: VenueStorefrontBookingSearchInitialValues | null): string {
    const queryParams = new URLSearchParams(query)
    queryParams.delete('setupBookingRequestId')
    queryParams.delete('setupCancelled')
    queryParams.delete('paymentIntentId')
    queryParams.delete('setupIntentId')
    queryParams.delete('startSeconds')
    queryParams.delete('availabilityTag')
    queryParams.delete('restrictedReasonIds')
    if (searchCriteria?.partySize !== undefined) {
        queryParams.set('partySize', searchCriteria.partySize.toString())
    } else {
        queryParams.delete('partySize')
    }
    if (searchCriteria?.date !== undefined) {
        queryParams.set(
            'date',
            Helper.makeLocalISOFormattedDateString(searchCriteria.date)
        )
    } else {
        queryParams.delete('date')
    }
    if (searchCriteria?.idealTime !== undefined) {
        queryParams.set(
            'idealTime',
            new Time(
                null,
                searchCriteria.idealTime.getHours(),
                searchCriteria.idealTime.getMinutes()
            ).toString()
        )
    } else {
        queryParams.delete('idealTime')
    }
    queryParams.delete('eid')
    queryParams.delete('eventId')
    if (searchCriteria?.eventId) {
        queryParams.set('eventId', searchCriteria.eventId)
    }
    if (searchCriteria?.autoOpen !== undefined) {
        queryParams.set('ao', searchCriteria.autoOpen.toString())
    }
    for (const key of ['requiresWheelchairAccess', 'requiresDogFriendly', 'requiresPramAccessible', 'requiresHighchairFriendly'] as const) {
        if (searchCriteria?.[key]) {
            queryParams.set(key, 'true')
        } else {
            queryParams.delete(key)
        }
    }
    if (searchCriteria?.restrictedReasonIds?.length) {
        queryParams.set('rrids', searchCriteria.restrictedReasonIds.join(','))
    } else {
        queryParams.delete('rrids')
    }
    if (searchCriteria?.areaId) {
        queryParams.set('areaId', searchCriteria.areaId)
    } else {
        queryParams.delete('areaId')
    }
    return queryParams.toString()
}

export function choiceTrackingContext(choice: VenueStorefrontBookingChoice): StorefrontBookingContext {
    if (choice.kind === 'standard') return {}
    return choice.kind === 'reason'
        ? { reasonId: choice.reason.id, reasonName: choice.reason.displayName }
        : { eventId: choice.event.id, eventName: choice.event.displayName }
}

export function bookingTrackingContext(state: VenueStorefrontBookingFlowState, configuredVenue: Venue | null): StorefrontBookingContext {
    const context = {
        partySize: state.searchCriteria?.partySize,
        slotTime: 'selectedSlot' in state ? state.selectedSlot?.bookingSlot.dateTime.toISOString() : undefined,
        durationMinutes: 'selectedSlot' in state ? state.selectedSlot?.reservableOption?.durationMinutes : undefined,
    }
    if (state.step === 'waiting' || state.step === 'confirmation') {
        return context
    }
    const reasonId = 'reasonId' in state ? state.reasonId : null
    const eventId = 'eventId' in state ? state.eventId : state.searchCriteria?.eventId
    const venue = 'availability' in state ? state.availability.venue : configuredVenue
    // Do not echo unrecognised launch-query values into telemetry.
    const reason = reasonId ? venue?.reasonWithId(reasonId) : null
    const event = eventId ? venue?.eventWithId(eventId) : null
    return {
        ...context,
        reasonId: reason?.id,
        reasonName: reason?.displayName,
        eventId: event?.id,
        eventName: event?.displayName,
        areaId: 'selectedSlot' in state
            ? state.selectedSlot?.reservableArea?.id ?? state.selectedSlot?.waitlistArea?.id
            : undefined,
    }
}
